import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import {
  ArrowLeft,
  Check,
  Clipboard,
  ExternalLink,
  FileText,
  FolderKanban,
  Link2,
  LogOut,
  Pencil,
  Plus,
  Save,
  Settings,
  Trash2,
  Upload,
} from 'lucide-react'
import { isSupabaseConfigured, supabase } from '../lib/supabase'
import { demoPortfolio } from '../data/demo'
import { CATEGORY_LABELS, type Profile, type Project, type ProjectCategory, type ShareLink } from '../types'

type AdminTab = 'projects' | 'profile' | 'links'

const blankProject: Omit<Project, 'id'> = {
  slug: '',
  title: '',
  category: 'game-interaction',
  summary: '',
  role: '',
  period: '',
  tags: [],
  cover_path: null,
  content: '',
  highlights: [],
  featured: false,
  status: 'draft',
  sort_order: 0,
}

const blankProfile: Profile = {
  name: '',
  initials: '',
  headline: '',
  bio: '',
  location: '',
  email: '',
  phone: '',
  availability: '',
  resume_path: null,
  years_experience: '',
  project_count: '',
}

function slugify(value: string) {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
    .replace(/^-|-$/g, '')
  return normalized || `project-${Date.now()}`
}

function SetupRequired() {
  return (
    <main className="admin-state">
      <div className="admin-state-card">
        <Settings size={28} />
        <p className="eyebrow">ONE-TIME SETUP</p>
        <h1>后台还差一步连接</h1>
        <p>前端已经可以运行。创建 Supabase 项目后，将项目地址与 anon key 写入 <code>.env.local</code>，再执行 <code>supabase/schema.sql</code>。</p>
        <a className="primary-button" href="/">先查看访客端</a>
      </div>
    </main>
  )
}

function Login({ onLogin }: { onLogin: (session: Session) => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!supabase) return
    setBusy(true)
    setMessage('')
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    setBusy(false)
    if (error) return setMessage(error.message)
    if (data.session) onLogin(data.session)
  }

  return (
    <main className="login-page">
      <a href="/" className="text-button"><ArrowLeft size={18} /> 返回作品集</a>
      <form className="login-card" onSubmit={submit}>
        <div className="admin-logo">P</div>
        <p className="eyebrow">PORTFOLIO STUDIO</p>
        <h1>个人管理端</h1>
        <p>只有你可以登录、上传和发布内容。</p>
        <label>邮箱<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" /></label>
        <label>密码<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="current-password" /></label>
        {message && <p className="form-error">{message}</p>}
        <button className="primary-button" type="submit" disabled={busy}>{busy ? '登录中…' : '进入管理后台'}</button>
      </form>
    </main>
  )
}

function ProjectEditor({
  initial,
  ownerId,
  onSaved,
  onCancel,
}: {
  initial: Project | null
  ownerId: string
  onSaved: () => void
  onCancel: () => void
}) {
  const [form, setForm] = useState<Omit<Project, 'id'>>(initial ? { ...initial } : { ...blankProject })
  const [tags, setTags] = useState(initial?.tags.join('，') || '')
  const [highlights, setHighlights] = useState(initial?.highlights.join('\n') || '')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  const update = <K extends keyof Omit<Project, 'id'>>(key: K, value: Omit<Project, 'id'>[K]) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const uploadCover = async (file: File) => {
    if (!supabase) return
    setBusy(true)
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-')
    const path = `${ownerId}/covers/${crypto.randomUUID()}-${safeName}`
    const { error } = await supabase.storage.from('portfolio-media').upload(path, file)
    setBusy(false)
    if (error) return setMessage(`上传失败：${error.message}`)
    update('cover_path', path)
    setMessage('封面已上传，保存项目后生效。')
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!supabase) return
    setBusy(true)
    setMessage('')
    const payload = {
      ...form,
      owner_id: ownerId,
      slug: form.slug || slugify(form.title),
      tags: tags.split(/[，,]/).map((tag) => tag.trim()).filter(Boolean),
      highlights: highlights.split('\n').map((item) => item.trim()).filter(Boolean),
    }
    const request = initial
      ? supabase.from('projects').update(payload).eq('id', initial.id)
      : supabase.from('projects').insert(payload)
    const { error } = await request
    setBusy(false)
    if (error) return setMessage(error.message)
    onSaved()
  }

  return (
    <form className="editor-panel" onSubmit={save}>
      <header><div><p className="eyebrow">PROJECT EDITOR</p><h2>{initial ? '编辑项目' : '新建项目'}</h2></div><button type="button" className="text-button" onClick={onCancel}>取消</button></header>
      <div className="form-grid">
        <label className="span-2">项目名称<input value={form.title} onChange={(event) => update('title', event.target.value)} required placeholder="例如：战斗信息系统重构" /></label>
        <label>项目分类<select value={form.category} onChange={(event) => update('category', event.target.value as ProjectCategory)}>{Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>时间<input value={form.period} onChange={(event) => update('period', event.target.value)} placeholder="2025 / 学生项目" /></label>
        <label className="span-2">一句话概述<textarea rows={2} value={form.summary} onChange={(event) => update('summary', event.target.value)} required /></label>
        <label>我的角色<input value={form.role} onChange={(event) => update('role', event.target.value)} placeholder="主交互设计" /></label>
        <label>标签（逗号分隔）<input value={tags} onChange={(event) => setTags(event.target.value)} placeholder="HUD，用户研究，多端适配" /></label>
        <label className="span-2">项目正文<textarea rows={7} value={form.content} onChange={(event) => update('content', event.target.value)} placeholder="说明背景、问题、过程与设计判断…" /></label>
        <label className="span-2">项目成果（每行一条）<textarea rows={4} value={highlights} onChange={(event) => setHighlights(event.target.value)} placeholder={'完成 3 轮可用性测试\n建立可复用规范'} /></label>
        <label>发布状态<select value={form.status} onChange={(event) => update('status', event.target.value as 'draft' | 'published')}><option value="draft">草稿</option><option value="published">已发布</option></select></label>
        <label>排序<input type="number" value={form.sort_order} onChange={(event) => update('sort_order', Number(event.target.value))} /></label>
        <label className="file-field span-2"><span>项目封面</span><div><Upload size={18} /> {form.cover_path ? '更换封面' : '上传图片'}<input type="file" accept="image/*" onChange={(event) => event.target.files?.[0] && uploadCover(event.target.files[0])} /></div>{form.cover_path && <small>{form.cover_path.split('/').pop()}</small>}</label>
        <label className="check-field span-2"><input type="checkbox" checked={form.featured} onChange={(event) => update('featured', event.target.checked)} /><span>设为精选项目</span></label>
      </div>
      {message && <p className={message.includes('失败') ? 'form-error' : 'form-success'}>{message}</p>}
      <footer><button type="button" className="secondary-button" onClick={onCancel}>取消</button><button className="primary-button" type="submit" disabled={busy}><Save size={17} /> {busy ? '保存中…' : '保存项目'}</button></footer>
    </form>
  )
}

function ProjectsPanel({ ownerId }: { ownerId: string }) {
  const [projects, setProjects] = useState<Project[]>([])
  const [editing, setEditing] = useState<Project | null | 'new'>(null)
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)

  const load = useCallback(async () => {
    if (!supabase) return
    setLoading(true)
    const { data } = await supabase.from('projects').select('*').order('sort_order').order('created_at', { ascending: false })
    setProjects((data || []) as Project[])
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  const remove = async (project: Project) => {
    if (!supabase || !window.confirm(`确定删除“${project.title}”吗？`)) return
    await supabase.from('projects').delete().eq('id', project.id)
    await load()
  }

  const importResumeContent = async () => {
    if (!supabase) return
    setImporting(true)
    const { resume_url: _resumeUrl, id: _profileId, ...profile } = demoPortfolio.profile
    const profileResult = await supabase.from('profiles').upsert(
      { ...profile, owner_id: ownerId, resume_path: null },
      { onConflict: 'owner_id' },
    )
    const projectRows = demoPortfolio.projects.map(({ id: _id, cover_url: _coverUrl, gallery: _gallery, ...project }) => ({
      ...project,
      owner_id: ownerId,
    }))
    const projectsResult = await supabase.from('projects').upsert(projectRows, { onConflict: 'owner_id,slug' })
    setImporting(false)
    if (profileResult.error || projectsResult.error) {
      window.alert(profileResult.error?.message || projectsResult.error?.message || '导入失败')
      return
    }
    await load()
  }

  if (editing) return <ProjectEditor initial={editing === 'new' ? null : editing} ownerId={ownerId} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await load() }} />

  return (
    <section className="admin-panel">
      <header><div><p className="eyebrow">CONTENT</p><h2>项目作品</h2><p>管理访客能够看到的案例内容。</p></div><button className="primary-button" onClick={() => setEditing('new')}><Plus size={17} /> 新建项目</button></header>
      {loading ? <p className="panel-empty">正在读取项目…</p> : projects.length === 0 ? <div className="panel-empty"><FolderKanban size={28} /><h3>还没有项目</h3><p>可以直接导入刚刚整理好的旧简历，也可以从零创建。</p><button className="primary-button" onClick={importResumeContent} disabled={importing}>{importing ? '导入中…' : '导入旧简历内容'}</button></div> : (
        <div className="admin-project-list">
          {projects.map((project, index) => (
            <article key={project.id}>
              <span className="row-index">{String(index + 1).padStart(2, '0')}</span>
              <div><span className={`status-dot ${project.status}`} /> <small>{CATEGORY_LABELS[project.category]} · {project.status === 'published' ? '已发布' : '草稿'}</small><h3>{project.title}</h3><p>{project.summary}</p></div>
              <div className="row-actions"><button onClick={() => setEditing(project)} aria-label="编辑"><Pencil size={17} /></button><button onClick={() => remove(project)} aria-label="删除"><Trash2 size={17} /></button></div>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}

function ProfilePanel({ ownerId }: { ownerId: string }) {
  const [form, setForm] = useState<Profile>(blankProfile)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!supabase) return
    supabase.from('profiles').select('*').maybeSingle().then(({ data }) => data && setForm(data as Profile))
  }, [])

  const update = (key: keyof Profile, value: string) => setForm((current) => ({ ...current, [key]: value }))

  const uploadResume = async (file: File) => {
    if (!supabase) return
    setBusy(true)
    const path = `${ownerId}/resume/${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '-')}`
    const { error } = await supabase.storage.from('portfolio-media').upload(path, file)
    setBusy(false)
    if (error) return setMessage(error.message)
    setForm((current) => ({ ...current, resume_path: path }))
    setMessage('简历已上传，请保存资料。')
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!supabase) return
    setBusy(true)
    const { error } = await supabase.from('profiles').upsert({ ...form, owner_id: ownerId }, { onConflict: 'owner_id' })
    setBusy(false)
    setMessage(error ? error.message : '个人资料已保存。')
  }

  return (
    <form className="admin-panel profile-panel" onSubmit={save}>
      <header><div><p className="eyebrow">PROFILE</p><h2>个人资料</h2><p>这些内容会出现在首页、关于页和联系方式中。</p></div><button className="primary-button" type="submit" disabled={busy}><Save size={17} /> 保存资料</button></header>
      <div className="form-grid">
        <label>展示姓名<input value={form.name} onChange={(event) => update('name', event.target.value)} required /></label>
        <label>姓名缩写<input value={form.initials} onChange={(event) => update('initials', event.target.value)} maxLength={3} placeholder="YN" /></label>
        <label className="span-2">职业标题<input value={form.headline} onChange={(event) => update('headline', event.target.value)} placeholder="游戏交互设计师 · 产品体验设计" /></label>
        <label className="span-2">个人简介<textarea rows={5} value={form.bio} onChange={(event) => update('bio', event.target.value)} /></label>
        <label>所在地<input value={form.location} onChange={(event) => update('location', event.target.value)} /></label>
        <label>联系邮箱<input type="email" value={form.email} onChange={(event) => update('email', event.target.value)} /></label>
        <label>联系电话<input value={form.phone || ''} onChange={(event) => update('phone', event.target.value)} /></label>
        <label>经验年数<input value={form.years_experience || ''} onChange={(event) => update('years_experience', event.target.value)} placeholder="5+" /></label>
        <label>项目数量<input value={form.project_count || ''} onChange={(event) => update('project_count', event.target.value)} placeholder="12" /></label>
        <label className="span-2">当前状态<input value={form.availability} onChange={(event) => update('availability', event.target.value)} placeholder="欢迎交流新的设计机会" /></label>
        <label className="file-field span-2"><span>完整简历（PDF）</span><div><Upload size={18} /> {form.resume_path ? '更换 PDF' : '上传 PDF'}<input type="file" accept="application/pdf" onChange={(event) => event.target.files?.[0] && uploadResume(event.target.files[0])} /></div>{form.resume_path && <small>{form.resume_path.split('/').pop()}</small>}</label>
      </div>
      {message && <p className={message.includes('已') ? 'form-success' : 'form-error'}>{message}</p>}
    </form>
  )
}

function LinksPanel({ ownerId }: { ownerId: string }) {
  const [links, setLinks] = useState<ShareLink[]>([])
  const [label, setLabel] = useState('作品集分享')
  const [days, setDays] = useState('7')
  const [copied, setCopied] = useState('')

  const load = useCallback(async () => {
    if (!supabase) return
    const { data } = await supabase.from('share_links').select('*').order('created_at', { ascending: false })
    setLinks((data || []) as ShareLink[])
  }, [])
  useEffect(() => { void load() }, [load])

  const create = async (event: FormEvent) => {
    event.preventDefault()
    if (!supabase) return
    const expiresAt = days === 'never' ? null : new Date(Date.now() + Number(days) * 86400000).toISOString()
    await supabase.from('share_links').insert({
      owner_id: ownerId,
      label,
      token: crypto.randomUUID().replaceAll('-', '') + crypto.randomUUID().replaceAll('-', ''),
      expires_at: expiresAt,
    })
    await load()
  }

  const getUrl = (token: string) => `${window.location.origin}/?token=${token}`
  const copy = async (token: string) => {
    await navigator.clipboard.writeText(getUrl(token))
    setCopied(token)
    window.setTimeout(() => setCopied(''), 1800)
  }
  const revoke = async (link: ShareLink) => {
    if (!supabase) return
    await supabase.from('share_links').update({ revoked_at: new Date().toISOString() }).eq('id', link.id)
    await load()
  }

  return (
    <section className="admin-panel links-panel">
      <header><div><p className="eyebrow">ACCESS</p><h2>分享链接</h2><p>为不同对象创建独立链接，并设置自动到期时间。</p></div></header>
      <form className="link-creator" onSubmit={create}>
        <label>备注<input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="某公司 HR" required /></label>
        <label>有效期<select value={days} onChange={(event) => setDays(event.target.value)}><option value="1">1 天</option><option value="3">3 天</option><option value="7">7 天</option><option value="30">30 天</option><option value="never">永久</option></select></label>
        <button className="primary-button" type="submit"><Link2 size={17} /> 生成链接</button>
      </form>
      <div className="share-list">
        {links.length === 0 ? <div className="panel-empty"><Link2 size={26} /><h3>还没有分享链接</h3><p>创建后即可复制给访客。</p></div> : links.map((link) => {
          const expired = Boolean(link.revoked_at || (link.expires_at && new Date(link.expires_at) < new Date()))
          return <article key={link.id} className={expired ? 'expired' : ''}>
            <div><span className={`status-dot ${expired ? 'draft' : 'published'}`} /><small>{expired ? '已失效' : '可访问'} · 已查看 {link.view_count || 0} 次</small><h3>{link.label}</h3><p>{link.expires_at ? `有效至 ${new Date(link.expires_at).toLocaleString('zh-CN')}` : '永久有效'}</p></div>
            <div className="row-actions">
              <a href={getUrl(link.token)} target="_blank" rel="noreferrer" aria-label="打开链接"><ExternalLink size={17} /></a>
              <button onClick={() => copy(link.token)} aria-label="复制链接">{copied === link.token ? <Check size={17} /> : <Clipboard size={17} />}</button>
              {!expired && <button onClick={() => revoke(link)} aria-label="立即失效"><Trash2 size={17} /></button>}
            </div>
          </article>
        })}
      </div>
    </section>
  )
}

export function AdminDashboard() {
  const [session, setSession] = useState<Session | null>(null)
  const [checking, setChecking] = useState(true)
  const [tab, setTab] = useState<AdminTab>('projects')

  useEffect(() => {
    if (!supabase) return setChecking(false)
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setChecking(false) })
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => setSession(nextSession))
    return () => listener.subscription.unsubscribe()
  }, [])

  const items = useMemo(() => [
    { id: 'projects' as const, label: '项目作品', icon: FolderKanban },
    { id: 'profile' as const, label: '个人资料', icon: FileText },
    { id: 'links' as const, label: '分享链接', icon: Link2 },
  ], [])

  if (!isSupabaseConfigured) return <SetupRequired />
  if (checking) return <main className="admin-state"><div className="loader" /></main>
  if (!session) return <Login onLogin={setSession} />

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-logo">P</div>
        <div><p className="eyebrow">PORTFOLIO</p><strong>内容工作台</strong></div>
        <nav>{items.map((item) => <button key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => setTab(item.id)}><item.icon size={18} /> {item.label}</button>)}</nav>
        <div className="sidebar-bottom"><a href="/" target="_blank"><ExternalLink size={17} /> 查看访客端</a><button onClick={() => supabase?.auth.signOut()}><LogOut size={17} /> 退出登录</button></div>
      </aside>
      <main className="admin-main">
        <div className="admin-mobile-head"><strong>作品集工作台</strong><a href="/">访客端</a></div>
        {tab === 'projects' && <ProjectsPanel ownerId={session.user.id} />}
        {tab === 'profile' && <ProfilePanel ownerId={session.user.id} />}
        {tab === 'links' && <LinksPanel ownerId={session.user.id} />}
      </main>
    </div>
  )
}
